import { Component, OnInit, Optional, signal } from '@angular/core';
import {
  FormControl,
  FormGroup,
  UntypedFormControl,
  UntypedFormGroup,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatDialogRef,
  MatDialogTitle,
  MatDialogContent,
  MatDialogActions,
  MatDialogClose,
} from '@angular/material/dialog';
import { CustomValidators } from 'angular-custom-validators';
import { dataStore } from 'src/app/app.component';
import { DataStoreExport } from 'src/app/interfaces';
import { CdkScrollable } from '@angular/cdk/scrolling';
import { MatFormField, MatLabel, MatInput, MatError, MatSuffix } from '@angular/material/input';
import { NgxIntlTelInputModule } from 'ngx-intl-tel-input';
import { MatSelect, MatOption } from '@angular/material/select';
import {
  MatDatepickerInput,
  MatDatepickerToggle,
  MatDatepicker,
} from '@angular/material/datepicker';
import { NgClass } from '@angular/common';
import { MatTooltip } from '@angular/material/tooltip';
import { MatIcon } from '@angular/material/icon';
import { MatButton } from '@angular/material/button';
import { formatNumericInput } from 'src/app/globals';
import { debounceTime } from 'rxjs';
import { PostcodeApiService } from 'src/app/services/postcode-api.service';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { MatSnackBar } from '@angular/material/snack-bar';
import { UserSettingsService } from 'src/app/services/user-settings.service';

@Component({
  selector: 'app-profile-edit',
  templateUrl: './profile-edit.component.html',
  styleUrls: ['./profile-edit.component.scss'],
  imports: [
    MatDialogTitle,
    CdkScrollable,
    MatDialogContent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    NgxIntlTelInputModule,
    MatError,
    MatSelect,
    MatOption,
    MatDatepickerInput,
    MatDatepickerToggle,
    MatSuffix,
    MatDatepicker,
    NgClass,
    MatTooltip,
    MatIcon,
    MatDialogActions,
    MatButton,
    MatDialogClose,
    MatProgressSpinner,
  ],
})
export class ProfileEditComponent implements OnInit {
  dataStore: DataStoreExport = dataStore;
  formatNumericInput = formatNumericInput;
  userForm: UntypedFormGroup = new FormGroup({
    name: new FormControl('', [Validators.required]),
    streetName: new FormControl('', [Validators.required]),
    postal: new FormControl('', [CustomValidators.postal, Validators.required]),
    houseNumber: new UntypedFormControl('', [
      Validators.required,
      CustomValidators.numberInput(false, false, 0),
      Validators.pattern(/^[0-9]*$/),
    ]),
    houseNumberAddition: new FormControl(),
    city: new FormControl('', [Validators.required]),
    particularityIds: new FormControl([]),
  });
  loading = signal(true);
  awaitingResponse = signal(false);

  constructor(
    public dialogRef: MatDialogRef<ProfileEditComponent>,
    private userSettingsService: UserSettingsService,
    @Optional() public postcodeApiService?: PostcodeApiService,
    @Optional() private snackBar?: MatSnackBar,
  ) {}

  async ngOnInit() {
    this.userForm.patchValue(dataStore.orgUser);
    if (dataStore.publicSettings.askBirthDay) {
      this.userForm.addControl('birthDate', new UntypedFormControl('', [Validators.required]));
      if (dataStore.orgUser.birthDate != null) {
        this.userForm.controls.birthDate.setValue(new Date(dataStore.orgUser.birthDate.toDate()));
      }
    }
    if (dataStore.publicSettings.askGender) {
      this.userForm.addControl('gender', new UntypedFormControl('', [Validators.required]));
      if (dataStore.orgUser.gender != null) {
        this.userForm.controls.gender.setValue(dataStore.orgUser.gender);
      }
    }
    const particularitiesArray: string[] = [...(this.dataStore?.orgUser?.particularityIds ?? [])];
    this.userForm.controls['particularityIds'].setValue(particularitiesArray);
    this.userForm.controls.postal.valueChanges.pipe(debounceTime(600)).subscribe(async () => {
      await this.getAddress();
    });
    this.userForm.controls.houseNumber.valueChanges.pipe(debounceTime(600)).subscribe(async () => {
      await this.getAddress();
    });
    this.loading.set(false);
  }

  toggleParticularity(particularityId: string) {
    const activeParticularities: string[] = [
      ...(this.userForm.get('particularityIds')?.value ?? []),
    ];
    const index = activeParticularities.indexOf(particularityId);
    if (index !== -1) {
      activeParticularities.splice(index, 1);
    } else {
      activeParticularities.push(particularityId);
    }
    this.userForm.controls['particularityIds'].setValue(activeParticularities);
  }

  async getAddress() {
    if (!this.postcodeApiService) {
      return;
    }
    if (
      this.userForm.value.postal !== '' &&
      this.userForm.controls.postal.valid &&
      this.userForm.value.houseNumber !== '' &&
      this.userForm.controls.houseNumber.valid
    ) {
      const addressSearchResult = await this.postcodeApiService.getAddress(
        this.userForm.value.postal,
        this.userForm.value.houseNumber,
      );
      if (addressSearchResult) {
        this.userForm.controls.streetName.patchValue(addressSearchResult.street);
        this.userForm.controls.city.patchValue(addressSearchResult.city);
      } else {
        this.userForm.controls.streetName.reset(null);
        this.userForm.controls.city.reset(null);
      }
    } else {
      this.userForm.controls.streetName.reset(null);
      this.userForm.controls.city.reset(null);
    }
  }

  async save() {
    console.log(this.userForm);
    if (
      this.awaitingResponse() ||
      !this.userForm.valid ||
      !this.userForm.controls.streetName.valid ||
      !this.userForm.controls.city.valid
    ) {
      this.userForm.markAllAsTouched();
      return;
    }
    this.awaitingResponse.set(true);
    const formData = this.userForm.getRawValue();
    if (formData.postal) {
      formData.postal = formData.postal.toUpperCase().replace(/\s/g, '');
    }
    try {
      await this.userSettingsService.update([dataStore.selectedOrganisation.id], formData);
      this.dialogRef.close();
    } catch (error) {
      this.awaitingResponse.set(false);
      this.snackBar?.open('Er is iets misgegaan, probeer het later opnieuw.', 'X');
      console.error(error);
    }
  }
}
